import { type ReactNode, useEffect } from "react";

import { useFeatureFlag } from "@vega-api/extended/auth/useFeatureFlag";
import {
  UiCapabilityKey,
  useUiCapabilities,
} from "@vega-api/extended/auth/useUiCapabilities";
import { useHandleNestedNav } from "@vega-core/components/Navigation/Sidebar/Sidebar.store";
import { subNavigationActions } from "@vega-core/components/Navigation/Sidebar/SubNavigation.store";
import { SubNavigationList } from "@vega-core/components/Navigation/Sidebar/SubNavigationList";
import { m } from "@vega-core/paraglide/messages";
import { buildTimeFlags } from "@vega-core/utils/buildTimeFlags";
import { isGPCoInvestPortal } from "@vega-core/utils/global";
import { GPSettingsFullPaths } from "@vega-modules/paths";

export const SettingNavigationWrapper = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { capabilities } = useUiCapabilities();
  const { featureFlag } = useFeatureFlag();
  const isStandaloneCoInvestPortal = isGPCoInvestPortal;
  const isKeyManagementEnabled =
    featureFlag["gp_api-gateway_workbench-key-management"];

  useEffect(() => {
    const items = [];

    // Hide Collaborators in standalone co-invest portal
    if (
      capabilities[UiCapabilityKey.CanViewWorkbenchCollaborators] &&
      !isStandaloneCoInvestPortal
    ) {
      items.push({
        href: GPSettingsFullPaths.SettingsCollaborators,
        name: m.settings_collaborators_title(),
      });
    }

    // Hide Case Management in standalone co-invest portal
    if (
      featureFlag.gp_case_management &&
      buildTimeFlags.gp_cases_page &&
      capabilities[UiCapabilityKey.CanConfigureCases] &&
      !isStandaloneCoInvestPortal
    ) {
      items.push({
        href: GPSettingsFullPaths.SettingsCaseManagement,
        name: m.settings_caseManagement_title(),
      });
    }

    if (
      featureFlag.lp_reporting_v2 &&
      buildTimeFlags.reporting_gp_settings_cas_management &&
      capabilities[UiCapabilityKey.CanManageFundCasConfigs] &&
      !isStandaloneCoInvestPortal
    ) {
      items.push({
        href: GPSettingsFullPaths.SettingsCASManagement,
        name: m.settings_casManagement_title(),
      });
    }

    // Hide Reporting API Key Mgmt. in standalone co-invest portal
    if (
      isKeyManagementEnabled &&
      capabilities[UiCapabilityKey.CanActAsVegaAdmin] &&
      !isStandaloneCoInvestPortal
    ) {
      items.push({
        href: GPSettingsFullPaths.SettingsReportingApiKeys,
        name: m.settings_reportingApiKeys_title(),
      });
    }

    // Co-investment settings:
    // - Always shown in standalone co-invest portal (no feature flag check)
    // - Only shown in full GP portal when feature flag is enabled
    if (
      isStandaloneCoInvestPortal ||
      (featureFlag.gp_co_investment && buildTimeFlags.gp_co_investment_page)
    ) {
      items.push({
        href: GPSettingsFullPaths.SettingsCoInvestment,
        name: m.settings_coInvestment_title(),
      });
    }

    items.push(
      {
        href: GPSettingsFullPaths.SettingsProfile,
        name: m.settings_profile(),
      },
      // Hide Notifications in standalone co-invest portal
      ...(!isStandaloneCoInvestPortal
        ? [
            {
              href: GPSettingsFullPaths.SettingsNotifications,
              name: m.titles_notifications(),
            },
          ]
        : []),
      {
        href: GPSettingsFullPaths.SettingsSupport,
        name: m.titles_support(),
      },
      {
        href: GPSettingsFullPaths.SettingsDisplay,
        name: m.settings_display(),
      },
    );

    subNavigationActions.setSubNav({
      node: (
        <SubNavigationList
          horizontal={isStandaloneCoInvestPortal}
          items={items}
        />
      ),
      items,
    });

    return () => {
      subNavigationActions.clean();
    };
  }, [
    capabilities[UiCapabilityKey.CanViewWorkbenchCollaborators],
    capabilities[UiCapabilityKey.CanActAsVegaAdmin],
    capabilities[UiCapabilityKey.CanManageFundCasConfigs],
    capabilities[UiCapabilityKey.CanConfigureCases],
    featureFlag.gp_case_management,
    isKeyManagementEnabled,
    featureFlag.gp_co_investment,
    featureFlag.lp_reporting_v2,
    isStandaloneCoInvestPortal,
  ]);
  useHandleNestedNav();

  return children;
};
